import type { AppState } from '../../state/appState.js';
import { resolveGeometrySlotFor, resolveMaterialSlotFor } from '../../state/layerGraphSelectors.js';
import { cloneNodes, cloneEdges } from '../documentClone.js';
import type {
  GeometrySlot,
  MaterialSlot,
} from '../../scene/layerState.js';
import type { AttachLibraryCmd, DetachLibraryCmd } from '../commandBuilders.js';
import type { ActionBase, ActionExecution } from '../commandExecutor.js';
import {
  cloneSlotValue,
  deepEqualValue,
  getLayerDefFor,
} from './shared.js';

export {
  autoDetachEditedComponentSlotIfNeededFor,
  editorAutoDetachComponentSlotHooks,
  autoForkEditedBuiltInLibraryIfNeededFor,
} from './componentSlotTransitions.js';

export interface ComponentSlotHooks {
  notifyEditorTargetSynced?: () => void;
  reloadComponentSlot?: (componentId: string | null, slot: 'geometry' | 'material', layerId?: string) => void;
}

export interface SetComponentSlotAction extends ActionBase<'component:setSlot'> {
  componentId: string | null;
  layerId: string;
  slot: 'geometry' | 'material';
  value: GeometrySlot | MaterialSlot;
}

export function execSetComponentSlotFor(
  appState: AppState,
  cmd: SetComponentSlotAction,
  hooks: ComponentSlotHooks = {},
): ActionExecution<void> {
  const layerDef = appState.layerLibrary[cmd.layerId];
  if (!layerDef) return { result: undefined, undoActions: [], changed: false };
  const previous = cmd.slot === 'geometry' ? layerDef.geometry : layerDef.material;
  const nextValue = cloneSlotValue(cmd.value);

  if (deepEqualValue(previous, nextValue)) {
    return { result: undefined, undoActions: [], changed: false };
  }

  if (cmd.slot === 'geometry') layerDef.geometry = nextValue as GeometrySlot;
  else layerDef.material = nextValue as MaterialSlot;
  if (
    appState.editorContext.mode === 'layer'
    && (appState.editorContext.componentId ?? null) === cmd.componentId
    && appState.editorContext.layerId === cmd.layerId
    && appState.editorContext.layerSlot === cmd.slot
  ) {
    hooks.reloadComponentSlot?.(cmd.componentId, cmd.slot, cmd.layerId);
    appState.editorGraphDirty = false;
    appState.selectedNode = null;
  }
  hooks.notifyEditorTargetSynced?.();

  return {
    result: undefined,
    undoActions: [{
      type: 'component:setSlot',
      componentId: cmd.componentId,
      layerId: cmd.layerId,
      slot: cmd.slot,
      value: cloneSlotValue(previous),
    }],
    changed: true,
  };
}

export function execAttachLibraryFor(appState: AppState, cmd: AttachLibraryCmd): ActionExecution<void> {
  const result = getLayerDefFor(appState, cmd.componentId, cmd.layerId);
  if (!result) return { result: undefined, undoActions: [], changed: false };
  const { layerDef, layerId } = result;
  const previous = cmd.slot === 'geometry' ? layerDef.geometry : layerDef.material;
  const nextValue = { type: 'library', id: cmd.libraryId } as const;

  if (cmd.slot === 'geometry') layerDef.geometry = nextValue;
  else layerDef.material = nextValue;

  return {
    result: undefined,
    undoActions: [{
      type: 'component:setSlot',
      componentId: cmd.componentId,
      layerId,
      slot: cmd.slot,
      value: cloneSlotValue(previous),
    }],
    changed: true,
  };
}

export function execDetachLibraryFor(appState: AppState, cmd: DetachLibraryCmd): ActionExecution<void> {
  const result = getLayerDefFor(appState, cmd.componentId, cmd.layerId);
  if (!result) return { result: undefined, undoActions: [], changed: false };
  const { layerDef, layerId } = result;

  const currentSlot = cmd.slot === 'geometry' ? layerDef.geometry : layerDef.material;
  if (currentSlot.type !== 'library') {
    return { result: undefined, undoActions: [], changed: false };
  }

  const resolved = cmd.slot === 'geometry'
    ? resolveGeometrySlotFor(appState, currentSlot)
    : resolveMaterialSlotFor(appState, currentSlot);
  if (!resolved) return { result: undefined, undoActions: [], changed: false };

  const nextValue = { type: 'inline', nodes: cloneNodes(resolved.nodes), edges: cloneEdges(resolved.edges) } as const;
  if (cmd.slot === 'geometry') layerDef.geometry = nextValue as GeometrySlot;
  else layerDef.material = nextValue as MaterialSlot;

  return {
    result: undefined,
    undoActions: [{
      type: 'component:setSlot',
      componentId: cmd.componentId,
      layerId,
      slot: cmd.slot,
      value: { type: 'library', id: currentSlot.id },
    }],
    changed: true,
  };
}
